<template>
  <div>
    <Nav />
    <!-- 主要内容区 -->
    <div class="flex pt-20 max-w-7xl mx-auto">
      <LeftNav activeItem="addresses" />
      <main class="flex-1 p-6">
        <div class="max-w-5xl">
          <div class="flex items-center justify-between mb-8">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">地址管理</h1>
              <p class="text-gray-600 mt-1">
                管理收货地址信息，确保租赁设备准确送达
              </p>
            </div>
            <button
              class="bg-primary text-white px-6 py-2 !rounded-button hover:bg-secondary transition-colors whitespace-nowrap add-address-btn"
              @click="openAddAddressModal"
            >
              <div
                class="w-5 h-5 flex items-center justify-center mr-2 inline-block float-left"
              >
                <i class="ri-add-line"></i>
              </div>
              新增地址
            </button>
          </div>
          <div class="space-y-4" id="address-list">
            <!-- 地址列表 -->
            <div v-if="addressList.length > 0">
              <div
                v-for="address in addressList"
                :key="address.id"
                class="bg-white rounded-xl shadow-sm border border-gray-100 address-item mb-4"
              >
                <div class="p-6">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center space-x-3 mb-3">
                        <h3 class="text-lg font-semibold text-gray-900">
                          {{ address.name }}
                        </h3>
                        <span
                          v-if="address.is_default === 1"
                          class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                          >默认地址</span
                        >
                        <span class="text-gray-600">{{
                          formatTel(address.tel)
                        }}</span>
                      </div>
                      <div class="text-gray-600 mb-4">
                        <p>
                          {{ address.region }}
                          {{ address.detail }}
                        </p>
                      </div>
                      <div class="flex items-center space-x-6">
                        <label
                          class="flex items-center space-x-2 cursor-pointer"
                        >
                          <div class="relative">
                            <input
                              type="checkbox"
                              class="sr-only default-switch"
                              :checked="address.is_default === 1"
                              @change="setDefaultAddress(address.id)"
                            />
                            <div
                              class="w-11 h-6 rounded-full shadow-inner transition-colors"
                              :class="{
                                'bg-primary': address.is_default === 1,
                                'bg-gray-300': address.is_default === 2,
                              }"
                            ></div>
                            <div
                              class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow transition-transform"
                              :class="{
                                'transform translate-x-5':
                                  address.is_default === 1,
                              }"
                            ></div>
                          </div>
                          <span class="text-sm text-gray-700"
                            >设为默认地址</span
                          >
                        </label>
                      </div>
                    </div>
                    <div class="flex items-center space-x-3">
                      <button
                        class="border border-gray-300 text-gray-700 px-4 py-2 !rounded-button hover:bg-gray-50 transition-colors whitespace-nowrap text-sm edit-address-btn"
                        @click="openEditAddressModal(address)"
                      >
                        <div
                          class="w-4 h-4 flex items-center justify-center mr-1 inline-block float-left"
                        >
                          <i class="ri-edit-line"></i>
                        </div>
                        编辑
                      </button>
                      <button
                        class="border border-red-300 text-red-600 px-4 py-2 !rounded-button hover:bg-red-50 transition-colors whitespace-nowrap text-sm delete-address-btn"
                        @click="deleteAddress(address.id)"
                      >
                        <div
                          class="w-4 h-4 flex items-center justify-center mr-1 inline-block float-left"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </div>
                        删除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 无地址状态 -->
            <div
              v-else
              class="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100"
            >
              <i class="ri-map-pin-line text-5xl text-gray-300 mb-4"></i>
              <p class="text-gray-500 mb-6">您还没有添加收货地址</p>
              <button
                class="bg-primary text-white px-6 py-2 !rounded-button hover:bg-secondary transition-colors whitespace-nowrap"
                @click="openAddAddressModal"
              >
                立即添加
              </button>
            </div>
          </div>
          <div class="bg-blue-50 rounded-xl p-6 border border-blue-100 mt-8">
            <div class="flex items-start space-x-4">
              <div
                class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"
              >
                <i class="ri-information-line text-blue-600 text-xl"></i>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">
                  地址管理说明
                </h3>
                <ul class="space-y-2 text-sm text-gray-600">
                  <li class="flex items-start">
                    <div
                      class="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"
                    ></div>
                    <span
                      >您最多可以保存 20
                      个收货地址，建议定期整理不常用的地址</span
                    >
                  </li>
                  <li class="flex items-start">
                    <div
                      class="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"
                    ></div>
                    <span
                      >默认地址将在下单时自动选中，您可以随时修改默认地址设置</span
                    >
                  </li>
                  <li class="flex items-start">
                    <div
                      class="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"
                    ></div>
                    <span
                      >为确保设备准时送达，请仔细核对地址信息的准确性和完整性</span
                    >
                  </li>
                  <li class="flex items-start">
                    <div
                      class="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"
                    ></div>
                    <span>如需配送到偏远地区，可能会产生额外的物流费用</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    <!-- 页脚 -->
    <Footer />

    <!-- 新增/编辑地址弹窗 -->
    <el-dialog
      :title="editMode ? '编辑地址' : '新增地址'"
      :visible.sync="dialogVisible"
      width="500px"
      :before-close="handleClose"
    >
      <el-form
        ref="addressForm"
        :model="addressForm"
        :rules="addressRules"
        label-width="100px"
        class="address-form"
        style="padding-right: 30px"
      >
        <el-form-item label="收货人" prop="name">
          <el-input
            v-model="addressForm.name"
            placeholder="请输入收货人姓名"
          ></el-input>
        </el-form-item>
        <el-form-item label="手机号码" prop="tel">
          <el-input
            v-model="addressForm.tel"
            placeholder="请输入手机号码"
          ></el-input>
        </el-form-item>
        <el-form-item label="所在地区" prop="region" style="width: 100%">
          <el-cascader
            v-model="addressForm.region"
            :options="regionOptions"
            :props="{
              expandTrigger: 'hover',
              value: 'name',
              label: 'name',
              children: 'children',
            }"
            placeholder="请选择省/市/区"
            style="width: 100%"
          ></el-cascader>
        </el-form-item>
        <el-form-item label="详细地址" prop="detail">
          <el-input
            v-model="addressForm.detail"
            type="textarea"
            :rows="3"
            placeholder="请输入详细地址信息"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Message, MessageBox } from "element-ui";
import Nav from "@/components/Nav.vue";
import Footer from "@/components/Footer.vue";
import LeftNav from "@/components/LeftNav.vue";
import api from "@/utils/api.js";

export default {
  name: "Addresses",
  components: {
    Nav,
    Footer,
    LeftNav,
  },
  data() {
    return {
      // 地址列表数据
      addressList: [],
      totalCount: 0,
      // 地址表单数据
      addressForm: {
        id: "",
        name: "",
        tel: "",
        province: "",
        city: "",
        district: "",
        detail: "",
        is_default: 2,
        region: [],
      },
      // 表单验证规则
      addressRules: {
        name: [
          { required: true, message: "请输入收货人姓名", trigger: "blur" },
          {
            min: 2,
            max: 20,
            message: "姓名长度在 2 到 20 个字符",
            trigger: "blur",
          },
        ],
        tel: [
          { required: true, message: "请输入手机号码", trigger: "blur" },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: "请输入正确的手机号码",
            trigger: "blur",
          },
        ],
        region: [
          { required: true, message: "请选择所在地区", trigger: "change" },
        ],
        detail: [
          { required: true, message: "请输入详细地址", trigger: "blur" },
          {
            min: 5,
            max: 200,
            message: "地址长度在 5 到 200 个字符",
            trigger: "blur",
          },
        ],
      },
      // 弹窗状态
      dialogVisible: false,
      // 编辑模式
      editMode: false,
      // 地区选项数据
      regionOptions: [],
    };
  },
  mounted() {
    this.loadAddressList();
    this.loadRegionOptions();
  },
  methods: {
    // 加载地址列表
    async loadAddressList() {
      try {
        const response = await api.goods.getAddressList();
        this.addressList = response.data.resdata || [];
        this.totalCount = response.data.restotal || 0;
      } catch (error) {
        console.error("获取地址列表失败:", error);
        this.addressList = [];
      }
    },

    // 加载地区选项
    async loadRegionOptions() {
      try {
        const response = await api.goods.getRegionOptions();
        this.regionOptions = response.data.resdata || [];
      } catch (error) {
        console.error("获取地区选项失败:", error);
        this.regionOptions = [];
      }
    },

    // 格式化手机号码
    formatTel(tel) {
      if (!tel) return "";
      return tel.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
    },

    // 打开新增地址弹窗
    openAddAddressModal() {
      this.editMode = false;
      this.resetForm();
      this.dialogVisible = true;
    },

    // 打开编辑地址弹窗
    openEditAddressModal(address) {
      this.editMode = true;
      this.addressForm = {
        id: address.id,
        name: address.name,
        tel: address.tel,
        province: address.province,
        city: address.city,
        district: address.district,
        detail: address.detail,
        is_default: address.is_default,
        region: [address.province, address.city, address.district],
      };
      this.dialogVisible = true;
    },

    // 重置表单
    resetForm() {
      this.addressForm = {
        id: "",
        name: "",
        tel: "",
        province: "",
        city: "",
        district: "",
        detail: "",
        is_default: 2,
        region: [],
      };
      if (this.$refs.addressForm) {
        this.$refs.addressForm.resetFields();
      }
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false;
      this.resetForm();
    },

    // 提交表单
    async submitForm() {
      try {
        await this.$refs.addressForm.validate();

        // 处理地区信息
        if (this.addressForm.region && this.addressForm.region.length > 0) {
          this.addressForm.province = this.addressForm.region[0];
          this.addressForm.city = this.addressForm.region[1];
          this.addressForm.district = this.addressForm.region[2];
        }

        let response;
        if (this.editMode) {
          // 编辑地址
          response = await api.goods.editAddress(this.addressForm);
        } else {
          // 新增地址
          response = await api.goods.addAddress(this.addressForm);
        }

        this.dialogVisible = false;
        this.loadAddressList();
      } catch (error) {
        console.error("提交表单失败:", error);
      }
    },

    // 设置默认地址
    async setDefaultAddress(addressId) {
      try {
        const response = await api.goods.setDefaultAddress({ id: addressId });
        this.loadAddressList();
      } catch (error) {
        console.error("设置默认地址失败:", error);
      }
    },

    // 删除地址
    deleteAddress(addressId) {
      MessageBox.confirm("确定要删除这个地址吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const response = await api.goods.removeAddress({ id: addressId });

            this.loadAddressList();
          } catch (error) {
            console.error("删除地址失败:", error);
          }
        })
        .catch(() => {
          Message.info("已取消删除");
        });
    },
  },
};
</script>

<style scoped>
.address-form {
  margin-top: 20px;
}

.dialog-footer {
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/pages/ucenter/Addresses.vue